/* the store storefront runtime. Authored locally; no remote theme engine.
 * Opt-in while the remaining components in docs/LOCAL-RUNTIME.md are migrated.
 */
function debounce(callback, wait = 300) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => callback.apply(this, args), wait);
  };
}

function getFocusableElements(container) {
  return [...container.querySelectorAll('a[href], button, input, select, textarea, summary, [tabindex]')]
    .filter(node => !node.disabled && node.tabIndex >= 0 && !node.closest('[hidden], [inert]') && node.getClientRects().length);
}

let storefrontFocusController;
function trapFocus(container, initial = container) {
  removeTrapFocus();
  if (!container) return;
  storefrontFocusController = new AbortController();
  container.addEventListener('keydown', event => {
    if (event.key !== 'Tab') return;
    const elements = getFocusableElements(container);
    const first = elements[0] || container;
    const last = elements.at(-1) || container;
    if (!elements.length || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    }
  }, { signal: storefrontFocusController.signal });
  initial?.focus();
}

function removeTrapFocus(returnTo) {
  storefrontFocusController?.abort();
  returnTo?.focus?.();
}

function onKeyUpEscape(event) {
  if (event.key !== 'Escape') return;
  const details = event.target.closest('details[open]');
  if (!details) return;
  details.removeAttribute('open');
  details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  details.querySelector('summary')?.focus();
}

function pauseAllMedia() {
  document.querySelectorAll('video').forEach(video => video.pause());
  document.querySelectorAll('product-model').forEach(model => model.modelViewerUI?.pause());
  document.querySelectorAll('.js-youtube').forEach(frame => frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }), '*'));
  document.querySelectorAll('.js-vimeo').forEach(frame => frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*'));
}

function fetchConfig(type = 'json') {
  return { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: `application/${type}` } };
}

// Mutations share a queue: an add and a quantity change must never race.
const StorefrontCart = {
  pending: Promise.resolve(),
  enqueue(task) {
    const result = this.pending.then(task);
    this.pending = result.catch(() => {});
    return result;
  },
  async request(url, body) {
    const multipart = body instanceof FormData;
    const response = await fetch(url, {
      method: 'POST',
      headers: multipart ? { Accept: 'application/json' } : fetchConfig().headers,
      body: multipart ? body : JSON.stringify(body)
    });
    const data = await response.json();
    if (!response.ok || data.status >= 400 || data.errors) {
      throw new Error(typeof data.errors === 'string' ? data.errors : data.description || window.cartStrings?.error || 'Cart update failed');
    }
    return data;
  },
  sections() {
    const sections = [{ id: 'cart-icon-bubble', selector: '#cart-icon-bubble' }];
    if (document.querySelector('cart-drawer')) sections.push({ id: 'cart-drawer', selector: 'cart-drawer' });
    for (const id of ['main-cart-items', 'main-cart-footer']) {
      const target = document.getElementById(id)?.closest('.shopify-section');
      if (target) sections.push({ id: target.id.replace('shopify-section-', ''), selector: `#${CSS.escape(target.id)}` });
    }
    return sections;
  },
  render(data, sections) {
    // A successful cart mutation can still return null section HTML. Reload the
    // cart view, never repeat the mutation (which could add the item twice).
    const updates = sections.map(section => {
      const html = data.sections?.[section.id];
      const parsed = html && new DOMParser().parseFromString(html, 'text/html');
      // The API always wraps a section in #shopify-section-<id>. The header renders
      // the cart bubble's contents inline under its own id instead, so the selector
      // matches in the page but never in the response; the wrapper is its counterpart.
      const incoming = parsed && (parsed.querySelector(section.selector) || parsed.getElementById(`shopify-section-${section.id}`));
      const current = document.querySelector(section.selector);
      if (!incoming || !current) throw new Error('Cart sections unavailable');
      return { incoming, current };
    });
    const wasOpen = document.querySelector('cart-drawer')?.classList.contains('active');
    updates.forEach(({ incoming, current }) => {
      if (current.matches('cart-drawer')) {
        current.classList.toggle('is-empty', incoming.classList.contains('is-empty'));
        current.innerHTML = incoming.innerHTML;
        if (wasOpen) current.open();
      } else current.innerHTML = incoming.innerHTML;
    });
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
  },
  cartURL() { return window.routes.cart_url; }
};

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.input = this.querySelector('input[type="number"]');
    if (!this.input) return;
    this.addEventListener('click', event => {
      const button = event.target.closest('button[name="plus"], button[name="minus"]');
      if (!button) return;
      event.preventDefault();
      const previous = this.input.value;
      button.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
      if (previous !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }, { signal: this.controller.signal });
  }
  disconnectedCallback() { this.controller?.abort(); }
}

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.addEventListener('click', event => {
      if (event.target.id === 'CartDrawer-Overlay' || event.target === this) this.close();
    }, { signal });
    this.addEventListener('keydown', event => { if (event.key === 'Escape') this.close(); }, { signal });
    document.addEventListener('click', event => {
      // sections/header.liquid puts the id on a div beside the icon, inside the link,
      // so a click on the icon itself never reaches #cart-icon-bubble. Bind the link.
      const link = event.target.closest('a.header__icon--cart, #cart-icon-bubble');
      if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.setActiveElement(link);
      this.open();
    }, { signal });
  }
  disconnectedCallback() { this.controller?.abort(); }
  setActiveElement(element) { this.activeElement = element; }
  open() {
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    // The drawer animates `visibility` over 0.3s and focus() on a hidden element is
    // a no-op, so the keyboard would stay on the page behind it. Wait for the
    // transition, with a timer for the case where none runs (reduced motion).
    clearTimeout(this.focusTimer);
    const focusDrawer = event => {
      // transitionend bubbles, and a child finishing its own transition while the
      // drawer is still hidden would focus nothing at all.
      if (event && event.target !== this) return;
      clearTimeout(this.focusTimer);
      this.removeEventListener('transitionend', focusDrawer);
      if (!this.classList.contains('active')) return;
      const dialog = this.querySelector('.drawer__inner-empty') || this.querySelector('.drawer__inner');
      if (dialog) trapFocus(dialog, this.querySelector('.drawer__close') || dialog);
    };
    this.addEventListener('transitionend', focusDrawer);
    this.focusTimer = setTimeout(focusDrawer, 400);
  }
  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    // Section rendering replaces the header icon; look it up again if needed.
    removeTrapFocus(this.activeElement?.isConnected ? this.activeElement : document.getElementById('cart-icon-bubble'));
  }
  getSectionsToRender() { return StorefrontCart.sections(); }
  renderContents(data, dontOpen = false) {
    StorefrontCart.render(data, this.getSectionsToRender());
    if (!dontOpen) this.open();
  }
}

class CartItems extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.addEventListener('change', event => {
      const input = event.target.closest('input[name="updates[]"]');
      if (!input || !input.checkValidity()) return;
      this.updateQuantity(input.dataset.index, Number(input.value), input.id);
    }, { signal });
    this.addEventListener('click', event => {
      const remove = event.target.closest('cart-remove-button');
      if (!remove) return;
      event.preventDefault();
      this.updateQuantity(remove.dataset.index, 0);
    }, { signal });
  }
  disconnectedCallback() { this.controller?.abort(); }
  updateQuantity(line, quantity, inputId) {
    if (this.busy) return;
    this.busy = true;
    const previous = document.getElementById(inputId)?.closest('[data-quantity]')?.dataset.quantity;
    const item = this.querySelector(`[data-index="${Number(line)}"]`);
    const key = item?.dataset.key;
    this.setAttribute('aria-busy', 'true');
    const controls = [...this.querySelectorAll('button, input')];
    const disabled = controls.map(control => control.disabled);
    controls.forEach(control => { control.disabled = true; });
    return StorefrontCart.enqueue(async () => {
      const sections = StorefrontCart.sections();
      const data = await StorefrontCart.request(window.routes.cart_change_url, {
        ...(key ? { id: key } : { line: Number(line) }), quantity, sections: sections.map(section => section.id), sections_url: location.pathname
      });
      try { StorefrontCart.render(data, sections); }
      catch { location.assign(StorefrontCart.cartURL()); return; }
      document.getElementById(inputId)?.focus();
    }).catch(error => {
      const target = this.querySelector(`#CartDrawer-LineItemError-${line} .cart-item__error-text, #Line-item-error-${line} .cart-item__error-text`);
      if (target) target.textContent = error.message;
      const input = document.getElementById(inputId);
      if (input && previous != null) input.value = previous;
    }).finally(() => {
      this.busy = false;
      this.removeAttribute('aria-busy');
      controls.forEach((control, index) => { control.disabled = disabled[index]; });
    });
  }
}

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.form = this.querySelector('form');
    // Cards and upsells render the variant id disabled and leave it to the engine
    // to enable on load. Without this the payload carries no id and handleSubmit
    // drops the request, so every quick add silently does nothing.
    this.form?.querySelectorAll('input[name="id"][disabled]').forEach(input => {
      if (input.value) input.disabled = false;
    });
    this.form?.addEventListener('submit', event => this.handleSubmit(event), { signal: this.controller.signal });
  }
  disconnectedCallback() { this.controller?.abort(); }
  async handleSubmit(event) {
    event?.preventDefault();
    if (this.busy || !this.form || !this.form.reportValidity()) return;
    const button = this.form.querySelector('[name="add"]');
    if (button?.disabled) return;
    const incomplete = this.customFields().find(field => !field.validate());
    const payload = new FormData(this.form);
    // Collapse the form's quantity controls to the one the shopper chose, so the
    // add and anything keyed off the quantity read the same number.
    if (quantityControls(this.form).length) payload.set('quantity', String(submittedQuantity(this.form)));
    const units = this.multiLineSelection();
    const quantity = Number(payload.get('quantity') || 1);
    // A bundle, or a quantity break whose units carry their own variants, brings
    // its own lines; its form keeps the single id empty and disabled on purpose.
    if (!units && (!payload.get('id') || !Number.isInteger(quantity) || quantity < 1)) return;
    let errorBox = this.querySelector('.product-form__error-message-wrapper');
    if (!errorBox) {
      errorBox = document.createElement('div');
      errorBox.className = 'product-form__error-message-wrapper';
      errorBox.setAttribute('role', 'alert');
      const message = document.createElement('span');
      message.className = 'product-form__error-message';
      errorBox.append(message);
      this.append(errorBox);
    }
    if (errorBox) errorBox.hidden = true;
    // A required custom field that is empty must stop the add, not send a blank
    // property. validate() has already marked the field itself.
    if (incomplete) {
      errorBox.hidden = false;
      errorBox.querySelector('.product-form__error-message').textContent =
        incomplete.dataset.atcErrorMsg || window.cartStrings?.error || 'Please complete the required fields';
      return;
    }
    this.busy = true;
    if (button) { button.disabled = true; button.classList.add('loading'); }
    const opener = document.activeElement;
    try {
      await StorefrontCart.enqueue(async () => {
        const sections = StorefrontCart.sections();
        payload.set('sections', sections.map(section => section.id).join(','));
        payload.set('sections_url', location.pathname);
        // These go in as one line per variant, keeping the line item properties
        // and the selling plan the form had collected.
        let data = units
          ? await StorefrontCart.request(window.routes.cart_add_url, {
            items: units.map(unit => ({ ...unit, ...this.lineExtras(payload) })),
            sections: sections.map(section => section.id),
            sections_url: location.pathname
          })
          : await StorefrontCart.request(window.routes.cart_add_url, payload);
        // The Cart API will not take a scalar id and an items array in one call, so
        // a gift the chosen quantity unlocked follows in a second request. It stays
        // inside this queued task, so nothing else can slip between the two.
        const gifts = this.unlockedGifts();
        if (gifts.length) {
          data = await StorefrontCart.request(window.routes.cart_add_url, {
            items: gifts, sections: sections.map(section => section.id), sections_url: location.pathname
          });
        }
        if (this.form.querySelector('[data-skip-cart="true"]')) {
          location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
          return;
        }
        try { StorefrontCart.render(data, sections); }
        catch { location.assign(StorefrontCart.cartURL()); return; }
        document.querySelector('quick-add-modal[open]')?.hide(true);
        const drawer = document.querySelector('cart-drawer');
        if (drawer) { drawer.setActiveElement(opener); drawer.open(); }
        else location.assign(StorefrontCart.cartURL());
      });
    } catch (error) {
      if (errorBox) {
        errorBox.hidden = false;
        errorBox.querySelector('.product-form__error-message').textContent = error.message;
      }
    } finally {
      this.busy = false;
      if (button) {
        const picker = this.closest('product-info')?.querySelector('variant-selects');
        button.disabled = !!picker && (picker.loading || (picker.request && !picker.currentVariant?.available));
        button.classList.remove('loading');
      }
    }
  }
  // Only the fields that named this form as theirs, so an upsell sitting in the
  // same section never inherits the main product's custom fields.
  customFields() {
    const scope = this.closest('product-info') || this.closest('.shopify-section');
    if (!scope) return [];
    return [...scope.querySelectorAll('custom-product-field')].filter(field => field.form?.() === this.form);
  }
  unlockedGifts() {
    const scope = this.closest('product-info') || this.closest('.shopify-section');
    if (!scope) return [];
    const gifts = [...scope.querySelectorAll('quantity-gifts')]
      .filter(element => element.form?.() === this.form)
      .flatMap(element => element.unlocked());
    const breaks = [...scope.querySelectorAll('quantity-breaks')]
      .filter(element => element.form?.() === this.form)
      .flatMap(element => [...element.querySelectorAll('quantity-break-upsells')])
      .flatMap(element => element.active?.() || []);
    return [...gifts, ...breaks];
  }
  multiLineSelection() {
    const bundle = this.closest('bundle-deals');
    if (bundle) return bundle.selection?.() || null;
    const scope = this.closest('product-info') || this.closest('.shopify-section');
    if (!scope) return null;
    const breaks = [...scope.querySelectorAll('quantity-breaks')].find(element => element.form?.() === this.form);
    return breaks?.selection?.() || null;
  }
  lineExtras(payload) {
    const extras = {};
    for (const [key, value] of payload.entries()) {
      const property = /^properties\[(.+)\]$/.exec(key);
      if (property) (extras.properties ||= {})[property[1]] = value;
    }
    const plan = payload.get('selling_plan');
    if (plan) extras.selling_plan = Number(plan);
    return extras;
  }
}

class SearchForm extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.input?.addEventListener('input', debounce(() => this.onChange(), 250), { signal: this.controller.signal });
    this.querySelector('form')?.addEventListener('reset', event => this.onFormReset(event), { signal: this.controller.signal });
  }
  constructor() {
    super();
    // main-search.js accesses input immediately after super().
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
  }
  disconnectedCallback() { this.controller?.abort(); }
  onChange() { this.resetButton?.classList.toggle('hidden', !this.input.value); }
  shouldResetForm() { return !this.querySelector('[aria-selected="true"] a'); }
  onFormReset(event) {
    if (!this.shouldResetForm()) { event.preventDefault(); return; }
    this.input.value = '';
    this.onChange();
    this.input.focus();
  }
}

class VariantSelects extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.variants = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '[]');
    this.addEventListener('change', () => this.onVariantChange(), { signal: this.controller.signal });
  }
  disconnectedCallback() { this.controller?.abort(); this.request?.abort(); }
  selectedOptions() {
    return [...this.querySelectorAll('.product-form__input')].map(group =>
      group.querySelector('select')?.value ?? group.querySelector('input:checked')?.value
    );
  }
  scope() { return this.closest('product-info') || this.closest('.shopify-section'); }
  buttons() {
    const id = this.dataset.section;
    return [...document.querySelectorAll(`[id="ProductSubmitButton-${CSS.escape(id)}"], [id="SectionAtcBtn-${CSS.escape(id)}"]`)];
  }
  async onVariantChange() {
    this.request?.abort();
    this.request = new AbortController();
    const request = this.request;
    const options = this.selectedOptions();
    const variant = this.variants.find(item => item.options.every((option, index) => option === options[index]));
    this.currentVariant = variant;
    this.loading = !!variant;
    // Updating the id synchronously prevents an add with the previous selection.
    const scope = this.scope();
    scope?.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach(input => {
      input.value = variant?.id || '';
      input.disabled = !variant;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    this.buttons().forEach(button => { button.disabled = true; });
    this.updateOptionStates(options);
    if (!variant) { this.setLabel(window.variantStrings.unavailable); return; }
    const url = new URL(this.dataset.url, location.origin);
    url.searchParams.set('variant', variant.id);
    url.searchParams.set('section_id', this.dataset.originalSection || this.dataset.section);
    try {
      const response = await fetch(url, { signal: request.signal });
      if (!response.ok) throw new Error('Variant rendering failed');
      const incoming = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (request !== this.request) return;
      const original = this.dataset.originalSection || this.dataset.section;
      // Server-rendered money, tax, inventory, SKU and button content preserve
      // the store's market, language and currency formatting.
      for (const prefix of ['price-', 'Sku-', 'Inventory-', 'ProductSubmitButton-', 'sticky-atc-price-', 'main-atc-price-', 'sticky-atc-separate-price-']) {
        const current = document.getElementById(prefix + this.dataset.section);
        const replacement = incoming.getElementById(prefix + original);
        if (current && replacement) {
          current.innerHTML = replacement.innerHTML.replaceAll(original, this.dataset.section);
          current.className = replacement.className;
        }
      }
      const serverButton = incoming.getElementById(`ProductSubmitButton-${original}`);
      this.buttons().forEach(button => { button.disabled = !variant.available || !!serverButton?.disabled; });
      this.loading = false;
      if (!variant.available) this.setLabel(window.variantStrings.soldOut);
      const quantity = document.getElementById(`Quantity-${this.dataset.section}`);
      const serverQuantity = incoming.getElementById(`Quantity-${original}`);
      if (quantity && serverQuantity) {
        for (const name of ['min', 'max', 'step', 'data-min', 'data-max']) {
          if (serverQuantity.hasAttribute(name)) quantity.setAttribute(name, serverQuantity.getAttribute(name));
          else quantity.removeAttribute(name);
        }
        if (!quantity.checkValidity()) quantity.value = serverQuantity.value;
      }
      document.querySelectorAll(`secondary-variant-select[data-section="${CSS.escape(this.dataset.section)}"], secondary-variant-select-separate[data-section="${CSS.escape(this.dataset.section)}"]`).forEach(picker => picker.sync(variant));
      if (variant.featured_media) {
        scope?.querySelector('media-gallery')?.setActiveMedia(`${this.dataset.section}-${variant.featured_media.id}`, true);
      }
      if (this.dataset.updateUrl !== 'false') {
        const page = new URL(location.href);
        page.searchParams.set('variant', variant.id);
        history.replaceState({}, '', page);
      }
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { sectionId: this.dataset.section, variant } }));
    } catch (error) {
      if (error.name === 'AbortError') return;
      // A normal navigation lets Shopify render the chosen variant even when
      // asynchronous section rendering fails. Never leave a stale purchase id.
      const page = new URL(this.dataset.url, location.origin);
      page.searchParams.set('variant', variant.id);
      location.assign(page);
    }
  }
  setLabel(text) {
    this.buttons().forEach(button => {
      const label = button.querySelector('.main-atc__label__text, .sticky-atc__label__text');
      if (label) label.textContent = text;
    });
  }
  updateOptionStates(options) {
    this.querySelectorAll('.product-form__input').forEach((group, index) => {
      group.querySelectorAll('input[type="radio"], option').forEach(input => {
        const matches = this.variants.filter(variant => variant.options[index] === input.value && variant.options.slice(0, index).every((value, position) => value === options[position]));
        const exists = matches.length > 0;
        const available = matches.some(variant => variant.available);
        const nodes = [input, input.closest('.color-swatch')].filter(Boolean);
        nodes.forEach(node => {
          node.classList.toggle('non-existent', !exists);
          node.classList.toggle('unavailable', exists && !available);
        });
        input.classList.toggle('disabled', !available);
        if (input.tagName === 'OPTION') {
          input.hidden = !exists || (!available && this.dataset.skipUnavailable === 'true');
          input.disabled = input.hidden;
        }
      });
    });
  }
}

class PredictiveSearch extends SearchForm {
  connectedCallback() {
    super.connectedCallback();
    this.results = this.querySelector('[data-predictive-search]');
    const { signal } = this.controller;
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape') this.close();
      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
      const options = [...(this.results?.querySelectorAll('[role="option"]') || [])];
      const selected = options.findIndex(option => option.getAttribute('aria-selected') === 'true');
      if (event.key === 'Enter') {
        if (selected >= 0) { event.preventDefault(); options[selected].querySelector('a, button')?.click(); }
        return;
      }
      if (!options.length) return;
      event.preventDefault();
      const next = (selected + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
      options.forEach((option, index) => option.setAttribute('aria-selected', index === next ? 'true' : 'false'));
      this.input.setAttribute('aria-activedescendant', options[next].id);
    }, { signal });
    this.addEventListener('focusout', () => queueMicrotask(() => {
      if (!this.contains(document.activeElement)) this.close();
    }), { signal });
  }
  disconnectedCallback() { super.disconnectedCallback(); this.request?.abort(); }
  async onChange() {
    super.onChange();
    this.request?.abort();
    const query = this.input.value.trim();
    if (!query) { this.close(); return; }
    this.request = new AbortController();
    const request = this.request;
    const url = new URL(window.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('section_id', 'predictive-search');
    this.setAttribute('loading', '');
    try {
      const response = await fetch(url, { signal: request.signal });
      if (!response.ok) throw new Error('Search unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const results = html.getElementById('shopify-section-predictive-search');
      if (request !== this.request || !results || !this.results) return;
      this.results.innerHTML = results.innerHTML;
      this.setAttribute('results', 'true');
      this.setAttribute('open', '');
      this.input.setAttribute('aria-expanded', 'true');
    } catch (error) {
      if (error.name !== 'AbortError') this.close();
    } finally {
      if (request === this.request) this.removeAttribute('loading');
    }
  }
  close() {
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  }
}

class CartNote extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.addEventListener('change', event => {
      const field = event.target;
      if (field.name !== 'note') return;
      const note = field.value;
      field.setCustomValidity('');
      StorefrontCart.enqueue(() => StorefrontCart.request(window.routes.cart_update_url, { note }))
        .catch(error => { field.setCustomValidity(error.message); field.reportValidity(); });
    }, { signal: this.controller.signal });
  }
  disconnectedCallback() { this.controller?.abort(); }
}

class SecondaryVariantSelect extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.addEventListener('change', () => {
      const picker = document.getElementById(`variant-selects-${this.dataset.section}`);
      if (!picker) return;
      const selected = this.querySelector('select option:checked');
      const combined = selected?.dataset.variantId;
      const variant = combined && picker.variants.find(item => String(item.id) === combined);
      const options = variant?.options || [...this.querySelectorAll('select')].map(select => select.value);
      picker.querySelectorAll('.product-form__input').forEach((group, index) => {
        const select = group.querySelector('select');
        if (select) select.value = options[index];
        group.querySelectorAll('input[type="radio"]').forEach(input => { input.checked = input.value === options[index]; });
      });
      picker.onVariantChange();
    }, { signal: this.controller.signal });
  }
  sync(variant) {
    const selects = this.querySelectorAll('select');
    if (selects.length === 1 && selects[0].querySelector('[data-variant-id]')) {
      const option = [...selects[0].options].find(option => option.dataset.variantId === String(variant.id));
      if (option) selects[0].value = option.value;
    } else selects.forEach((select, index) => { select.value = variant.options[index]; });
  }
  disconnectedCallback() { this.controller?.abort(); }
}

class StickyAtc extends HTMLElement {
  connectedCallback() {
    const button = document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
    if (!button || this.dataset.afterScroll !== 'true') return;
    this.observer?.disconnect();
    this.observer = new IntersectionObserver(entries => {
      const visible = !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0;
      this.style.transform = visible ? 'translateY(0)' : '';
    });
    this.observer.observe(button);
  }
  disconnectedCallback() { this.observer?.disconnect(); }
}

class InternalVideo extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.video = this.querySelector('video');
    if (!this.video) return;
    const { signal } = this.controller;
    this.addEventListener('click', event => {
      const button = event.target.closest('button');
      if (!button) return;
      if (button.matches('.internal-video__sound-btn')) {
        event.preventDefault();
        this.video.muted = !this.video.muted;
        this.classList.toggle('internal-video--muted', this.video.muted);
      } else if (button.matches('.internal-video__play')) {
        event.preventDefault();
        if (this.video.paused) this.play(); else this.video.pause();
      } else if (button.matches('.internal-video__fullscreen-btn')) {
        event.preventDefault();
        if (document.fullscreenElement) document.exitFullscreen?.();
        else this.video.requestFullscreen?.().catch(() => {});
      }
    }, { signal });
    this.video.addEventListener('play', () => this.classList.add('internal-video--playing'), { signal });
    this.video.addEventListener('pause', () => this.classList.remove('internal-video--playing'), { signal });
    this.video.addEventListener('timeupdate', () => {
      const progress = this.querySelector('.internal-video__timeline');
      if (progress && this.video.duration) progress.style.width = `${100 * this.video.currentTime / this.video.duration}%`;
    }, { signal });
    document.addEventListener('visibilitychange', () => { if (document.hidden) this.video.pause(); }, { signal });
    this.observer?.disconnect();
    this.observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && this.getClientRects().length) {
        this.load();
        if (this.dataset.autoplay === 'true' && !matchMedia('(prefers-reduced-motion: reduce)').matches && !document.hidden) {
          this.video.muted = true;
          this.play();
        }
      } else this.video.pause();
    });
    this.observer.observe(this);
  }
  load() {
    let changed = false;
    this.video.querySelectorAll('source[data-src]').forEach(source => {
      source.src = source.dataset.src;
      delete source.dataset.src;
      changed = true;
    });
    if (changed) this.video.load();
  }
  play() { this.load(); this.video.play()?.catch(() => this.classList.remove('internal-video--playing')); }
  disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); this.video?.pause(); }
}

class CardVariantSelects extends VariantSelects {
  scope() { return this.closest('.card-wrapper'); }
  buttons() { return [...(this.scope()?.querySelectorAll('[name="add"]') || [])]; }
  async onVariantChange() {
    const options = this.selectedOptions();
    const variant = this.variants.find(item => item.options.every((option, index) => option === options[index]));
    const scope = this.scope();
    scope?.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach(input => {
      input.value = variant?.id || '';
      input.disabled = !variant;
    });
    this.buttons().forEach(button => { button.disabled = !variant?.available; });
    if (!variant) return;
    // Card selectors stay local to their card; they must not change the PDP URL.
    scope?.querySelectorAll('a[href]').forEach(link => {
      const url = new URL(link.href, location.origin);
      if (url.pathname === this.dataset.url) {
        url.searchParams.set('variant', variant.id);
        link.href = url;
      }
    });
    const image = scope?.querySelector('.card__media img');
    if (image && variant.featured_image?.src) {
      image.src = variant.featured_image.src;
      image.removeAttribute('srcset');
      image.alt = variant.featured_image.alt || image.alt;
    }
  }
}

// quantity-breaks and the subscription widget both hand over shop.money_format, so
// their amounts can be built exactly as Liquid would rather than copied out of the
// DOM. These are the placeholders Shopify defines; anything else is left alone.
const MONEY_PLACEHOLDERS = {
  amount: [2, ',', '.'],
  amount_no_decimals: [0, ',', '.'],
  amount_with_comma_separator: [2, '.', ','],
  amount_no_decimals_with_comma_separator: [0, '.', ','],
  amount_with_apostrophe_separator: [2, "'", '.'],
  amount_no_decimals_with_space_separator: [0, ' ', '.'],
  amount_with_space_separator: [2, ' ', ','],
  amount_with_period_and_space_separator: [2, ' ', '.']
};

function formatMoney(format, cents, stripZeros = false) {
  if (!format) return '';
  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => {
    const rule = MONEY_PLACEHOLDERS[name];
    if (!rule) return match;
    const [decimals, group, point] = rule;
    const amount = (Math.round(cents) / 100).toFixed(decimals);
    const [whole, fraction] = amount.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, group);
    // money_without_trailing_zeros drops the fraction only when it is all zeros.
    if (!fraction || (stripZeros && Number(fraction) === 0)) return grouped;
    return grouped + point + fraction;
  });
}

// sections/bundle-deals.liquid leaves its form's id empty and disabled: the bundle
// is several products at once, so the lines can only be assembled from the
// checkboxes. Each row's price span holds exactly one amount, which is the sample
// the money formatter needs — this section carries no money format of its own.
class BundleDeals extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.addEventListener('change', event => {
      const group = event.target.closest?.('.bundle-deals__variant-selects-js');
      if (group) this.onVariantChange(group);
      else if (event.target.closest?.('.bundle-deals__checkbox-js')) this.refresh();
    }, { signal: this.controller.signal });
    this.refresh();
  }
  disconnectedCallback() { this.controller?.abort(); }
  rows() { return [...this.querySelectorAll('.bundle-deals__product-js')]; }
  box(row) { return row.querySelector('.bundle-deals__checkbox-js'); }
  onVariantChange(group) {
    const variants = JSON.parse(group.querySelector('script[type="application/json"]')?.textContent || '[]');
    const chosen = [...group.querySelectorAll('select')].map(select => select.value);
    const variant = variants.find(item => item.options.every((option, index) => option === chosen[index]));
    const row = group.closest('.bundle-deals__product-js');
    const box = row && this.box(row);
    if (!variant || !box) return;
    const left = Number(group.dataset.percentageLeft);
    const fixed = Number(group.dataset.fixedDiscount);
    // Mirrors sections/bundle-deals.liquid for a single row.
    const price = Math.max(0, Math.round(variant.price * (Number.isFinite(left) ? left : 1) - (Number.isFinite(fixed) ? fixed : 0)));
    const compare = (variant.compare_at_price || 0) > price ? variant.compare_at_price : variant.price;
    box.dataset.id = variant.id;
    box.dataset.price = price;
    box.dataset.comparePrice = compare;
    const priceNode = row.querySelector('.bundle-deals__price-js');
    const compareNode = row.querySelector('.bundle-deals__compare-price-js');
    if (priceNode) priceNode.textContent = formatLikeMoney(priceNode.textContent, price);
    if (compareNode) {
      compareNode.textContent = compare > price
        ? formatLikeMoney(compareNode.textContent.trim() || priceNode?.textContent || '', compare)
        : '';
    }
    const media = this.querySelectorAll('.bundle-deals__media-item-img-js')[Number(group.dataset.index)];
    if (media && variant.featured_image?.src) {
      media.src = variant.featured_image.src;
      media.removeAttribute('srcset');
      media.alt = variant.featured_image.alt || media.alt;
    }
    this.refresh();
  }
  refresh() {
    const chosen = this.rows().map(row => this.box(row)).filter(box => box?.checked);
    for (const row of this.rows()) {
      const box = this.box(row);
      if (box) box.dataset.checked = String(box.checked);
    }
    const left = Number(this.dataset.percentageLeft);
    const fixed = Number(this.dataset.fixedDiscount);
    const sum = chosen.reduce((total, box) => total + (Number(box.dataset.price) || 0), 0);
    const total = Math.max(0, Math.round(sum * (Number.isFinite(left) ? left : 1) - (Number.isFinite(fixed) ? fixed : 0)));
    const compare = chosen.reduce((amount, box) => amount + (Number(box.dataset.comparePrice) || 0), 0);
    const totalNode = this.querySelector('.bundle-deals__total-price-js');
    const compareNode = this.querySelector('.bundle-deals__total-compare-price-js');
    // The totals always follow the selection: a total that ignored an unticked
    // product would simply be wrong, whatever the price-changes setting says.
    if (totalNode) totalNode.textContent = formatLikeMoney(totalNode.textContent, total);
    if (compareNode) {
      compareNode.textContent = compare > total
        ? formatLikeMoney(compareNode.textContent.trim() || totalNode?.textContent || '', compare)
        : '';
    }
    this.querySelectorAll('[name="add"]').forEach(button => { button.disabled = !chosen.length; });
  }
  selection() {
    const items = this.rows()
      .map(row => this.box(row))
      .filter(box => box?.checked && Number(box.dataset.id))
      .map(box => ({ id: Number(box.dataset.id), quantity: 1 }));
    return items.length ? items : null;
  }
}

// Quantity breaks put their tiers in radios that sit outside the form and join it
// through `form=`, while the buy button keeps a hidden quantity input of its own.
// A form can therefore carry two controls named "quantity", and which one a plain
// FormData yields depends on their order in the document — the tier comes first
// here, but that is luck, not a rule. The tier the shopper picked is the answer,
// and ProductForm writes it back into the payload so the add cannot disagree.
function quantityControls(form) {
  return form ? [...form.elements].filter(control => control.name === 'quantity') : [];
}

function submittedQuantity(form) {
  const controls = quantityControls(form);
  const picked = controls.find(control => control.type === 'radio' && control.checked)
    || controls.find(control => control.type !== 'radio');
  return Number(picked?.value) || 1;
}

// The product blocks all hang off the main product form, never off an upsell's
// form that happens to sit in the same section.
function mainCartForm(element) {
  const scope = element.closest('product-info') || element.closest('.shopify-section');
  return scope?.querySelector('product-form.main-product-form form[action*="/cart/add"]')
    || scope?.querySelector('form[action*="/cart/add"]')
    || null;
}

// snippets/text-with-price.liquid is the placeholder language these blocks share.
// Re-rendering it in the browser is the only way a price can follow a choice the
// page was not built with.
function priceTokens({ quantity = 1, price, compare, name = '', subscription = '', money }) {
  const saved = compare - price;
  return {
    '[quantity]': String(quantity),
    '[name]': name,
    '[subscription_name]': subscription,
    '[price]': money(price),
    '[compare_price]': money(compare),
    '[amount_saved]': money(saved),
    '[amount_saved_rounded]': money(Math.round(saved / 100) * 100),
    '[price_each]': money(price / quantity),
    '[compare_price_each]': money(compare / quantity),
    '[percentage_saved]': `${compare ? Math.round((saved / compare) * 100) : 0}%`
  };
}

function renderPriceText(nodes, tokens) {
  for (const node of nodes) {
    let text = node.dataset.text;
    if (text == null) continue;
    for (const [token, value] of Object.entries(tokens)) text = text.split(token).join(value);
    node.innerHTML = text;
  }
}

// blocks/product_subscription.liquid renders nothing unless the product has selling
// plan groups, so this only runs on a store with a subscriptions app configured.
// Each frequency carries the price Liquid worked out for it, which is why the
// prices here are read off the chosen plan rather than recomputed.
class SubscriptionWidget extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.addEventListener('click', event => {
      const box = event.target.closest('.subscription__box');
      if (!box || event.target.closest('.subscription__frequency-radio, select')) return;
      const checkbox = this.dataset.isCheckbox === 'true' && box.dataset.type === 'subscription';
      this.select(checkbox && box.dataset.selected === 'true' ? 'one_time' : box.dataset.type);
    }, { signal });
    this.addEventListener('change', event => {
      if (event.target.matches('.subscription__frequency-input, select')) this.apply();
    }, { signal });
    this.select(this.querySelector('.subscription__box[data-selected="true"]')?.dataset.type
      || this.dataset.preselected
      || 'subscription');
  }
  disconnectedCallback() { this.controller?.abort(); this.mirror?.remove(); }
  chosenPlan() {
    return this.querySelector('.subscription__frequency-input:checked')
      || this.querySelector('select')?.selectedOptions[0]
      || null;
  }
  select(type) {
    for (const box of this.querySelectorAll('.subscription__box')) {
      const on = box.dataset.type === type;
      box.dataset.selected = String(on);
      box.classList.toggle('subscription__box--selected', on);
    }
    this.apply();
  }
  apply() {
    const form = mainCartForm(this);
    const subscribing = this.querySelector('.subscription__box[data-selected="true"]')?.dataset.type === 'subscription';
    const plan = subscribing
      ? (this.chosenPlan()?.dataset.sellingPlanId || this.dataset.firstSellingPlanId)
      : '';
    if (!form || !plan) {
      this.mirror?.remove();
      this.mirror = null;
    } else {
      if (!this.mirror || !this.mirror.isConnected) {
        this.mirror = document.createElement('input');
        this.mirror.type = 'hidden';
        this.mirror.name = 'selling_plan';
        form.append(this.mirror);
      }
      this.mirror.value = plan;
    }
    this.renderPrices();
  }
  renderPrices() {
    const format = this.dataset.moneyFormat;
    if (this.dataset.updatePrices !== 'true' || !format) return;
    const money = cents => formatMoney(format, cents, true);
    const subscription = this.dataset.subscriptionName || '';
    const base = Number(this.dataset.basePrice) || 0;
    const baseCompare = Number(this.dataset.baseComparePrice) || base;
    renderPriceText(this.querySelectorAll('.one-time-price-update[data-text]'),
      priceTokens({ price: base, compare: baseCompare, subscription, money }));
    const plan = this.chosenPlan();
    const price = Number(plan?.dataset.price);
    if (!Number.isFinite(price)) return;
    const compare = Number(plan?.dataset.comparePrice);
    renderPriceText(this.querySelectorAll('.sub-price-update[data-text]'),
      priceTokens({ price, compare: Number.isFinite(compare) && compare > price ? compare : baseCompare, subscription, money }));
  }
}

// snippets/quantity-breaks.liquid already works without JavaScript for the part
// that matters most: every tier is a radio named "quantity" bound to the product
// form through `form=`, and base.css both styles the selection and reveals that
// tier's pickers. What has no owner is the rest — the per-unit variant choice,
// which reaches no form at all, and the prices, which Liquid could only render for
// the variant that was selected when the page was built.
class QuantityBreaks extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.variants = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '[]');
    this.addEventListener('change', event => {
      const item = event.target.closest?.('.quantity-break__selector-item');
      if (item) this.onUnitChange(item);
    }, { signal });
    const scope = this.scope();
    scope?.addEventListener('change', event => {
      if (event.target.matches?.('input[name="id"]')) this.onVariantChange(Number(event.target.value));
    }, { signal });
  }
  disconnectedCallback() { this.controller?.abort(); }
  scope() { return this.closest('product-info') || this.closest('.shopify-section'); }
  form() {
    const scope = this.scope();
    return scope?.querySelector('product-form.main-product-form form[action*="/cart/add"]')
      || scope?.querySelector('form[action*="/cart/add"]')
      || null;
  }
  variantFor(options) {
    return this.variants.find(variant => variant.options.every((option, index) => option === options[index]));
  }
  onUnitChange(item) {
    const chosen = [...item.querySelectorAll('.quantity-break__variant-select')].map(select => select.value);
    const variant = this.variantFor(chosen);
    // An impossible combination leaves the previous id in place rather than
    // sending nothing, which the Cart API would reject for the whole line.
    if (variant) item.dataset.selectedId = variant.id;
    if (this.dataset.updateUnavailable === 'true') this.markUnavailable(item, chosen);
  }
  markUnavailable(item, chosen) {
    const selects = [...item.querySelectorAll('.quantity-break__variant-select')];
    selects.forEach((select, index) => {
      for (const option of select.options) {
        const probe = chosen.slice(0, index).concat(option.value);
        const matches = this.variants.filter(variant => probe.every((value, position) => variant.options[position] === value));
        const exists = matches.length > 0;
        const available = matches.some(variant => variant.available);
        option.classList.toggle('non-existent', !exists);
        option.classList.toggle('unavailable', exists && !available);
        const hide = !exists || (!available && this.dataset.skipUnavailable === 'true');
        option.disabled = hide;
        option.hidden = hide;
      }
    });
  }
  onVariantChange(id) {
    const variant = this.variants.find(item => item.id === id);
    if (!variant) return;
    this.dataset.basePrice = variant.price;
    for (const item of this.querySelectorAll('.quantity-break__selector-item')) item.dataset.selectedId = variant.id;
    if (this.dataset.updatePrices === 'true') {
      for (const label of this.querySelectorAll('.quantity-break')) this.renderPrices(label, variant);
    }
  }
  renderPrices(label, variant) {
    const format = this.dataset.moneyFormat;
    if (!format) return;
    const quantity = Number(label.dataset.quantity) || 1;
    const left = Number(label.dataset.percentageLeft);
    const fixed = Number(label.dataset.fixedDiscount);
    // Mirrors snippets/quantity-breaks.liquid exactly.
    const price = Math.round(variant.price * quantity * (Number.isFinite(left) ? left : 1) - (Number.isFinite(fixed) ? fixed : 0));
    const base = label.dataset.compareMode === 'compare_price' && (variant.compare_at_price || 0) > variant.price
      ? variant.compare_at_price
      : variant.price;
    const compareTotal = base * quantity;
    const money = cents => formatMoney(format, cents, true);
    renderPriceText(label.querySelectorAll('.variant-price-update[data-text]'),
      priceTokens({ quantity, price, compare: compareTotal, name: variant.title || '', money }));
    label.querySelector('.quantity-break__compare-price')?.classList.toggle('hidden', compareTotal <= price);
  }
  // One cart line per distinct variant the shopper picked across the tier's units.
  // Null means there are no per-unit pickers, and the plain quantity add is right.
  selection() {
    const checked = this.querySelector('input[name="quantity"]:checked');
    const units = [...(checked?.nextElementSibling?.querySelectorAll('.quantity-break__selector-item') || [])]
      .map(item => Number(item.dataset.selectedId))
      .filter(Boolean);
    if (!units.length) return null;
    const grouped = new Map();
    for (const id of units) grouped.set(id, (grouped.get(id) || 0) + 1);
    return [...grouped].map(([id, quantity]) => ({ id, quantity }));
  }
}

// Each of these sits inside one tier's <label>, so the tier that owns it is simply
// the radio in front of that label. A gift (data-toggle="false") is carried by the
// tier itself; a checkbox upsell is the shopper's to turn on and off.
class QuantityBreakUpsells extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.addEventListener('click', event => {
      const upsell = event.target.closest('.quantity-break__upsell[data-toggle="true"]');
      // No preventDefault: the click also lands on the surrounding label, and
      // picking the tier that the upsell belongs to is the right thing to happen.
      if (upsell) upsell.dataset.selected = upsell.dataset.selected === 'true' ? 'false' : 'true';
    }, { signal: this.controller.signal });
  }
  disconnectedCallback() { this.controller?.abort(); }
  active() {
    const input = this.closest('.quantity-break')?.previousElementSibling;
    if (!input || input.type !== 'radio' || !input.checked) return [];
    return [...this.querySelectorAll('.quantity-break__upsell')]
      .filter(upsell => upsell.dataset.selected === 'true' && Number(upsell.dataset.product))
      .map(upsell => ({ id: Number(upsell.dataset.product), quantity: 1 }));
  }
}

// sections/promo-popup.liquid renders nothing at all when the mode is disabled, and
// pre-opens the modal itself in the two test modes. So this only has to handle the
// enabled case: wait, honour the frequency, run the countdown and close.
class PromoPopup extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.overlay = this.querySelector('.sign-up-popup-overlay');
    this.modal = this.querySelector('.sign-up-popup-modal');
    this.addEventListener('click', event => {
      if (!event.target.closest('.promp-popup__close-btn')) return;
      event.preventDefault();
      this.close();
    }, { signal });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape') this.close();
    }, { signal });
    if (this.dataset.testMode === 'true') { this.countdown(); return; }
    if (this.suppressed()) return;
    this.opener = setTimeout(() => this.open(), (Number(this.dataset.delaySeconds) || 5) * 1000);
  }
  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.opener);
    clearInterval(this.ticker);
  }
  key() { return `storefront:promo-popup:${this.closest('.shopify-section')?.id || 'default'}`; }
  suppressed() {
    // A browser that refuses storage should still see the popup; the frequency is a
    // courtesy, and failing it closed would hide the section from those shoppers.
    try { return Number(localStorage.getItem(this.key())) > Date.now(); } catch { return false; }
  }
  open() {
    this.overlay?.classList.add('popup-overlay--active');
    this.modal?.classList.add('popup-modal--active');
    if (this.modal) trapFocus(this.modal, this.modal.querySelector('.popup-modal__close') || this.modal);
    this.countdown();
  }
  close() {
    if (!this.modal?.classList.contains('popup-modal--active')) return;
    this.overlay?.classList.remove('popup-overlay--active');
    this.modal.classList.remove('popup-modal--active');
    removeTrapFocus();
    clearInterval(this.ticker);
    if (this.dataset.testMode === 'true') return; // Test mode shows on every visit.
    const days = Number(this.dataset.delayDays) || 30;
    try { localStorage.setItem(this.key(), String(Date.now() + days * 86400000)); } catch { /* shows again */ }
  }
  countdown() {
    if (this.dataset.displayTimer !== 'true') return;
    const minutes = this.querySelector('.popup-modal__timer__minutes');
    const seconds = this.querySelector('.popup-modal__timer__seconds');
    if (!minutes || !seconds) return;
    let remaining = Math.round((Number(this.dataset.timerDuration) || 3) * 60);
    const tick = () => {
      minutes.textContent = String(Math.floor(Math.max(0, remaining) / 60)).padStart(2, '0');
      seconds.textContent = String(Math.max(0, remaining) % 60).padStart(2, '0');
      if (remaining <= 0) clearInterval(this.ticker);
      remaining -= 1;
    };
    clearInterval(this.ticker);
    tick();
    this.ticker = setInterval(tick, 1000);
  }
}

// snippets/quantity-gifts.liquid sits on the product page: each tier unlocks at a
// quantity of the main product. The cart hides the gift's quantity and remove
// controls, so a gift is only ever placed by the add it rides along with — there is
// no markup anywhere that would take one back out again.
class QuantityGifts extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    // The tier radios are joined to the form by `form=` but live outside it, so
    // their change event never bubbles through the form. Listen on the section.
    const scope = this.scope();
    scope?.addEventListener('change', () => this.sync(), { signal });
    scope?.addEventListener('input', () => this.sync(), { signal });
    this.sync();
  }
  disconnectedCallback() { this.controller?.abort(); }
  scope() { return this.closest('product-info') || this.closest('.shopify-section'); }
  form() {
    const scope = this.scope();
    return scope?.querySelector('product-form.main-product-form form[action*="/cart/add"]')
      || scope?.querySelector('form[action*="/cart/add"]')
      || null;
  }
  sync() {
    const quantity = submittedQuantity(this.form());
    for (const gift of this.querySelectorAll('.quantity-gift')) {
      gift.dataset.unlocked = String(quantity >= (Number(gift.dataset.quantity) || 0));
    }
  }
  unlocked() {
    return [...this.querySelectorAll('.quantity-gift')]
      .filter(gift => gift.dataset.unlocked === 'true' && Number(gift.dataset.product))
      .map(gift => ({ id: Number(gift.dataset.product), quantity: 1 }));
  }
}

// snippets/cart-gift.liquid is rendered inside the cart drawer, so Liquid already
// computes the threshold, the progress copy and the unlocked state on every cart
// change. The only thing left that Liquid cannot do is put the gift in the cart,
// which is why the snippet carries a hidden submit button and hides the gift's own
// cart line. The gift is excluded from the measure: counting it would let the gift
// keep itself unlocked, and the reconcile would never settle.
class CartDrawerGift extends HTMLElement {
  connectedCallback() { this.reconcile(); }
  measure(items) {
    return this.dataset.type === 'product_quantity'
      ? items.reduce((total, item) => total + item.quantity, 0)
      : items.reduce((total, item) => total + item.final_line_price, 0);
  }
  async reconcile() {
    const handle = this.dataset.handle;
    const variant = Number(this.dataset.id);
    const requirement = Number(this.dataset.requirement);
    if (!handle || !variant || !Number.isFinite(requirement)) return;
    try {
      await StorefrontCart.enqueue(async () => {
        const root = window.Shopify?.routes?.root || '/';
        const cart = await fetch(`${root}cart.js`).then(response => response.json());
        const gift = cart.items.find(item => item.handle === handle);
        const bought = cart.items.filter(item => item.handle !== handle);
        const main = this.dataset.mainProduct;
        const qualifies = this.measure(bought) >= requirement
          && (!main || bought.some(item => item.handle === main));
        if (qualifies === !!gift) return;
        const sections = StorefrontCart.sections();
        const body = { sections: sections.map(section => section.id), sections_url: location.pathname };
        const data = qualifies
          ? await StorefrontCart.request(window.routes.cart_add_url, { ...body, items: [{ id: variant, quantity: 1 }] })
          : await StorefrontCart.request(window.routes.cart_change_url, { ...body, id: gift.key, quantity: 0 });
        this.dataset.selected = String(qualifies);
        try { StorefrontCart.render(data, sections); } catch { /* The next page view renders it. */ }
      });
    } catch { /* A gift that cannot be added must never block the cart. */ }
  }
}

// snippets/custom-product-field.liquid renders the control with no `name` at all,
// so nothing it collects would ever reach the cart. The value travels as a line
// item property, mirrored into a hidden input inside the product form it belongs
// to — which is the main form, never an upsell's form in the same section.
class CustomProductField extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.addEventListener('change', () => this.sync(), { signal });
    this.addEventListener('input', () => this.sync(), { signal });
    this.sync();
  }
  disconnectedCallback() { this.controller?.abort(); this.mirror?.remove(); }
  scope() { return this.closest('product-info') || this.closest('.shopify-section'); }
  form() {
    const scope = this.scope();
    if (!scope) return null;
    return scope.querySelector('product-form.main-product-form form[action*="/cart/add"]')
      || [...scope.querySelectorAll('form[action*="/cart/add"]')].find(form => !form.closest('[data-is-cart-upsell]'))
      || null;
  }
  value() {
    const checked = this.querySelector('input[type="radio"]:checked');
    if (checked) return checked.value;
    const control = this.querySelector('input[type="text"], input[type="number"], textarea, select');
    return control ? control.value : (this.dataset.defaultValue || '');
  }
  sync() {
    this.setError(false);
    const form = this.form();
    const name = this.dataset.name;
    if (!form || !name) return;
    const value = this.value().trim();
    if (!value) { this.mirror?.remove(); this.mirror = null; return; }
    if (!this.mirror || !this.mirror.isConnected) {
      this.mirror = document.createElement('input');
      this.mirror.type = 'hidden';
      form.append(this.mirror);
    }
    // An empty property would still show as a blank line in the cart, so the input
    // only exists while there is something to send.
    this.mirror.name = `properties[${name}]`;
    this.mirror.value = value;
  }
  setError(state) {
    const message = this.querySelector('.input-error-msg');
    // base.css shows the message through `.input--error + .input-error-msg`.
    message?.previousElementSibling?.classList.toggle('input--error', state);
  }
  validate() {
    const control = this.querySelector('input[type="text"], input[type="number"], textarea, select');
    const empty = !this.value().trim();
    const invalid = (this.dataset.required === 'true' && empty)
      || (control && control.willValidate && !control.checkValidity());
    this.setError(!!invalid);
    return !invalid;
  }
}

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function ordinal(day) {
  const teen = day % 100;
  if (teen >= 11 && teen <= 13) return `${day}th`;
  return day + (['th', 'st', 'nd', 'rd'][day % 10] || 'th');
}

// snippets/estimated-shipping.liquid, snippets/shipping-checkpoints.liquid and
// blocks/product_estimated-shipping.liquid all render this element. Liquid writes a
// dash in place of each date, because a delivery estimate baked into a cached page
// would go stale; the dates have to be counted in the shopper's own clock.
class DynamicDates extends HTMLElement {
  connectedCallback() { this.render(); }
  list(name, fallback) {
    const parts = (this.dataset[name] || '').split(',').map(part => part.trim()).filter(Boolean);
    return parts.length === fallback.length ? parts : fallback;
  }
  render() {
    const days = this.list('dayLabels', WEEKDAYS.map(day => day[0].toUpperCase() + day.slice(1)));
    const months = this.list('monthLabels', MONTHS);
    // The setting asks for English day names, but a merchant who translated the
    // labels is likely to skip days by the names they just typed, so accept both.
    const named = new Set((this.dataset.daysToSkip || '').split(',').map(part => part.trim().toLowerCase()).filter(Boolean));
    const skipDays = new Set();
    WEEKDAYS.forEach((day, index) => {
      if (named.has(day) || named.has(days[index].toLowerCase())) skipDays.add(index);
    });
    const skipDates = new Set((this.dataset.datesToSkip || '').split(',').map(part => part.trim()).filter(Boolean));
    for (const node of this.querySelectorAll('[data-dynamic-date="true"]')) {
      const template = node.dataset.text;
      if (template == null) continue;
      const start = this.arrival(Number(node.dataset.minDays) || 0, skipDays, skipDates);
      const end = this.arrival(Number(node.dataset.maxDays) || 0, skipDays, skipDates);
      node.innerHTML = template
        .split('[start_date]').join(this.format(start, days, months))
        .split('[end_date]').join(this.format(end, days, months));
    }
  }
  skipped(date, skipDays, skipDates) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return skipDays.has((date.getDay() + 6) % 7) || skipDates.has(`${month}-${day}`);
  }
  arrival(count, skipDays, skipDates) {
    const date = new Date();
    date.setHours(12, 0, 0, 0); // Midday, so a DST shift cannot move the day.
    let remaining = Math.max(0, count);
    // A merchant can skip every weekday; stop rather than count forever.
    let guard = 0;
    while (remaining > 0 && guard < 3650) {
      date.setDate(date.getDate() + 1);
      guard += 1;
      if (!this.skipped(date, skipDays, skipDates)) remaining -= 1;
    }
    while (this.skipped(date, skipDays, skipDates) && guard < 3650) {
      date.setDate(date.getDate() + 1);
      guard += 1;
    }
    return date;
  }
  format(date, days, months) {
    const name = days[(date.getDay() + 6) % 7];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const padded = String(day).padStart(2, '0');
    const numeric = String(date.getMonth() + 1).padStart(2, '0');
    switch (this.dataset.dateFormat) {
      case 'day_dd_mm': return `${name}, ${day}. ${month}`;
      case 'mm_dd': return `${month} ${ordinal(day)}`;
      case 'dd_mm': return `${day}. ${month}`;
      case 'dd_mm_no_dot': return `${day} ${month}`;
      case 'day_dd_mm_numeric': return `${name}, ${padded}. ${numeric}.`;
      case 'dd_mm_numeric': return `${padded}. ${numeric}.`;
      default: return `${name}, ${month} ${ordinal(day)}`;
    }
  }
}

// sections/comparison-slider.liquid stacks the two images and lays a range input
// over them at opacity 0, which is what the shopper actually drags. base.css pins
// the overlay and the line at 50%, so the position has to be written inline.
class ComparisonSlider extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.input = this.querySelector('.comparison-slider__input');
    this.input?.addEventListener('input', () => this.move(), { signal: this.controller.signal });
    this.move();
  }
  disconnectedCallback() { this.controller?.abort(); }
  move() {
    const value = Math.min(100, Math.max(0, Number(this.input?.value ?? 50)));
    const position = `${value}%`;
    this.style.setProperty('--position', position);
    const overlay = this.querySelector('.comparison-slider__overlay');
    const line = this.querySelector('.comparison-slider__line');
    if (overlay) overlay.style.width = position;
    if (line) line.style.left = position;
  }
}

// blocks/product_clickable-discount.liquid swaps the two labels on [data-applied]
// and shows the error paragraph that sits next to it on [data-error].
class ClickableDiscount extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.button = this.querySelector('.clickable-discount__btn');
    this.button?.addEventListener('click', event => {
      event.preventDefault();
      this.apply();
    }, { signal: this.controller.signal });
  }
  disconnectedCallback() { this.controller?.abort(); }
  async apply() {
    const code = this.dataset.code;
    if (!code || this.dataset.applied === 'true' || this.dataset.loading === 'true') return;
    this.dataset.loading = 'true';
    this.dataset.error = 'false';
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}discount/${encodeURIComponent(code)}`);
      if (!response.ok) throw new Error('Discount could not be applied');
      // Shopify answers this route with a redirect to the storefront whether or not
      // the code exists, so "applied" means the request went through, not that the
      // code is valid. An unknown code simply does nothing at checkout.
      this.dataset.applied = 'true';
      this.button?.setAttribute('aria-pressed', 'true');
    } catch {
      this.dataset.error = 'true';
    } finally {
      this.dataset.loading = 'false';
    }
  }
}

// snippets/upsell-block.liquid opens this tag as `<{{ type }}-upsell>`, so the tag
// name never appears literally in the theme and a text search of the templates will
// not find it. The markup it wraps carries a variant <select>, a price, a toggle
// control and a sibling product-form that the add button posts through by `form=`.
class Upsell extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.picker = this.querySelector('.upsell__variant-picker');
    this.variants = JSON.parse(this.picker?.querySelector('script[type="application/json"]')?.textContent || '[]');
    this.addEventListener('change', event => {
      if (event.target.closest('.variant-dropdown')) this.onVariantChange();
    }, { signal });
    this.addEventListener('click', event => {
      if (!event.target.closest('.upsell-toggle-btn, .upsell__toggle-switch, .upsell__checkbox, .upsell__plus-btn')) return;
      event.preventDefault();
      if (this.dataset.toggle === 'true') this.onToggle();
    }, { signal });
  }
  disconnectedCallback() { this.controller?.abort(); }

  forms() { return [...this.querySelectorAll('product-form form')]; }
  currentVariant() {
    const chosen = [...(this.picker?.querySelectorAll('.variant-dropdown') || [])].map(select => select.value);
    if (!chosen.length) return this.variants.find(variant => String(variant.id) === this.dataset.id);
    return this.variants.find(variant => variant.options.every((option, index) => option === chosen[index]));
  }
  onVariantChange() {
    const variant = this.currentVariant();
    this.dataset.id = variant?.id || '';
    this.forms().forEach(form => form.querySelectorAll('input[name="id"]').forEach(input => {
      input.value = variant?.id || '';
      input.disabled = !variant;
    }));
    this.querySelectorAll('[name="add"]').forEach(button => { button.disabled = !variant?.available; });
    this.updatePrice(variant);
    const image = this.querySelector('.upsell__image img');
    if (image && variant?.featured_image?.src) {
      image.src = variant.featured_image.src;
      image.removeAttribute('srcset');
      image.alt = variant.featured_image.alt || image.alt;
    }
  }

  // Mirrors snippets/upsell-block.liquid: price = variant price * percentage_left
  // - fixed_discount, compare = the higher of price and compare_at_price. Blocks
  // that leave the discount settings off emit no attributes, so the defaults below
  // reproduce the undiscounted Liquid result.
  updatePrice(variant) {
    const box = this.querySelector('.upsell__price');
    if (!box || !variant) return;
    const left = Number(this.dataset.percentageLeft);
    const fixed = Number(this.dataset.fixedDiscount);
    const price = Math.max(0, Math.round(variant.price * (Number.isFinite(left) ? left : 1) - (Number.isFinite(fixed) ? fixed : 0)));
    const compare = Math.max(variant.price, variant.compare_at_price || 0);
    const regular = box.querySelector('.regular-price');
    const comparison = box.querySelector('.compare-price');
    if (regular) regular.textContent = formatLikeMoney(regular.textContent, price);
    if (comparison) {
      comparison.textContent = formatLikeMoney(comparison.textContent, compare);
      comparison.classList.toggle('hidden', !(compare > price));
    }
  }

  async onToggle() {
    if (this.busy) return;
    const variant = this.currentVariant();
    if (!variant) return;
    const selected = this.dataset.selected === 'true';
    this.busy = true;
    this.classList.add('loading');
    try {
      await StorefrontCart.enqueue(async () => {
        const sections = StorefrontCart.sections();
        const body = { sections: sections.map(section => section.id), sections_url: location.pathname };
        const data = selected
          ? await StorefrontCart.request(window.routes.cart_change_url, { ...body, id: String(variant.id), quantity: 0 })
          : await StorefrontCart.request(window.routes.cart_add_url, { ...body, items: [{ id: variant.id, quantity: 1 }] });
        this.dataset.selected = selected ? 'false' : 'true';
        try { StorefrontCart.render(data, sections); }
        catch { location.assign(StorefrontCart.cartURL()); }
      });
    } catch {
      this.dataset.selected = String(selected);
    } finally {
      this.busy = false;
      this.classList.remove('loading');
    }
  }
}

// A price under a thousand shows no thousands separator, so the sample alone cannot
// say what it is. Any larger amount Liquid rendered on the page answers it; when the
// page has none, the amount goes out ungrouped rather than guessed wrong.
let moneyGroupingCache;
function moneyGrouping() {
  if (moneyGroupingCache !== undefined) return moneyGroupingCache;
  moneyGroupingCache = '';
  for (const element of document.querySelectorAll('.price, .regular-price, .compare-price, .totals__total-value, .cart-item__price')) {
    const found = /\d{1,3}([.,   ])\d{3}(?!\d)/.exec(element.textContent);
    if (found) { moneyGroupingCache = found[1]; break; }
  }
  return moneyGroupingCache;
}

// The upsell price is the only money this runtime writes. Rather than reimplement
// shop.money_format and its market overrides, it copies the separators, decimals
// and surrounding text out of the amount Liquid already rendered in that same slot.
function formatLikeMoney(sample, cents) {
  const match = String(sample).match(/\d[\d.,   ]*\d|\d/);
  if (!match) return sample;
  const number = match[0];
  const decimals = /[.,](\d{2})$/.exec(number);
  const group = (number.slice(0, decimals ? -3 : undefined).match(/[.,   ]/) || [moneyGrouping()])[0];
  const amount = (cents / 100).toFixed(decimals ? 2 : 0);
  const [whole, fraction] = amount.split('.');
  const grouped = group ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, group) : whole;
  const rebuilt = fraction ? grouped + decimals[0][0] + fraction : grouped;
  return String(sample).slice(0, match.index) + rebuilt + String(sample).slice(match.index + number.length);
}

for (const [name, element] of Object.entries({
  'quantity-input': QuantityInput,
  'cart-drawer': CartDrawer,
  'cart-items': CartItems,
  'cart-drawer-items': class extends CartItems {},
  'product-form': ProductForm,
  'search-form': SearchForm,
  'variant-selects': VariantSelects,
  'predictive-search': PredictiveSearch,
  'cart-note': CartNote,
  'sticky-atc': StickyAtc,
  'secondary-variant-select': SecondaryVariantSelect,
  'secondary-variant-select-separate': class extends SecondaryVariantSelect {},
  'internal-video': InternalVideo,
  'card-variant-selects': CardVariantSelects,
  'cart-drawer-upsell': Upsell,
  'product-info-upsell': class extends Upsell {},
  'dynamic-dates': DynamicDates,
  'comparison-slider': ComparisonSlider,
  'clickable-discount': ClickableDiscount,
  'custom-product-field': CustomProductField,
  'cart-drawer-gift': CartDrawerGift,
  'quantity-gifts': QuantityGifts,
  'promo-popup': PromoPopup,
  'quantity-breaks': QuantityBreaks,
  'quantity-break-upsells': QuantityBreakUpsells,
  'theme-subscription-widget': SubscriptionWidget,
  'bundle-deals': BundleDeals
})) {
  if (!customElements.get(name)) customElements.define(name, element);
}
